import { create } from 'zustand';
import type { ARQrParams, UnlockedEntry } from '../qr/types';
import { entryKey } from '../qr/parser';

const STORAGE_KEY = 'ms_qr.unlocked';
const STORAGE_VERSION = 2;

type StorageShape = {
  version: number;
  entries: UnlockedEntry[];
};

function loadFromStorage(): UnlockedEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: StorageShape = JSON.parse(raw);
    if (parsed.version !== STORAGE_VERSION) return [];
    return Array.isArray(parsed.entries) ? parsed.entries : [];
  } catch {
    return [];
  }
}

function saveToStorage(entries: UnlockedEntry[]): void {
  const payload: StorageShape = { version: STORAGE_VERSION, entries };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

type Store = {
  entries: UnlockedEntry[];
  unlock: (params: ARQrParams) => 'new' | 'existing';
  remove: (params: ARQrParams) => void;
  clear: () => void;
};

export const useUnlocked = create<Store>((set, get) => ({
  entries: loadFromStorage(),
  unlock: (params) => {
    const key = entryKey(params);
    const found = get().entries.find((e) => entryKey(e) === key);
    if (found) return 'existing';
    const entry: UnlockedEntry = { ...params, unlockedAt: new Date().toISOString() };
    const next = [...get().entries, entry];
    saveToStorage(next);
    set({ entries: next });
    return 'new';
  },
  remove: (params) => {
    const key = entryKey(params);
    const next = get().entries.filter((e) => entryKey(e) !== key);
    saveToStorage(next);
    set({ entries: next });
  },
  clear: () => {
    saveToStorage([]);
    set({ entries: [] });
  },
}));
